import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Paper, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import CropSquareIcon from "@material-ui/icons/CropSquare";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

const Ticket = lazy(() => import("../Ticket"));

const MIN_W = 360;
const MIN_H = 420;

const useStyles = makeStyles((theme) => ({
  window: {
    position: "fixed",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 14,
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.mode === "dark"
        ? "0 18px 50px rgba(0,0,0,0.55)"
        : "0 18px 50px rgba(15,23,42,0.28)",
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    pointerEvents: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minHeight: 40,
    padding: "0 6px 0 10px",
    cursor: "move",
    userSelect: "none",
    backgroundColor: theme.mode === "dark" ? "#111827" : "#f8f9fc",
    borderBottom: `1px solid ${theme.mode === "dark" ? "#1f2937" : "#eef0f5"}`,
    flex: "none",
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 700,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  resizeHandle: {
    position: "absolute",
    right: 0,
    bottom: 0,
    width: 16,
    height: 16,
    cursor: "nwse-resize",
  },
}));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const fitToViewport = (x, y, width, height, minimized) => {
  const margin = 8;
  const headerH = 40;
  const topOffset = 56;
  const bottomSafe = 28;
  const viewportW = (window.visualViewport && window.visualViewport.width) || window.innerWidth;
  const viewportH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
  const maxW = Math.max(280, viewportW - margin * 2);
  const maxH = Math.max(240, viewportH - margin - topOffset - bottomSafe);
  const nextW = clamp(width, Math.min(MIN_W, maxW), maxW);
  const nextH = minimized ? headerH : clamp(height, Math.min(MIN_H, maxH), maxH);
  const nextX = clamp(x, margin, Math.max(margin, viewportW - nextW - margin));
  const nextY = clamp(
    y,
    topOffset + margin,
    Math.max(topOffset + margin, viewportH - nextH - bottomSafe)
  );
  return { x: nextX, y: nextY, width: nextW, height: nextH };
};

const TicketPopupWindow = ({ popup, onClose, onFocus }) => {
  const classes = useStyles();
  const initial = fitToViewport(
    popup.x || 72,
    popup.y || 72,
    popup.width || 420,
    popup.height || 480,
    false
  );
  const [pos, setPos] = useState({ x: initial.x, y: initial.y });
  const [size, setSize] = useState({
    width: initial.width,
    height: initial.height,
  });
  const [minimized, setMinimized] = useState(false);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      const next = fitToViewport(pos.x, pos.y, size.width, size.height, minimized);
      setPos({ x: next.x, y: next.y });
      if (!minimized) setSize({ width: next.width, height: next.height });
    };
    window.addEventListener("resize", onResize);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", onResize);
    };
  }, [minimized, pos.x, pos.y, size.height, size.width]);

  const startDrag = useCallback(
    (e) => {
      if (e.button !== 0) return;
      onFocus?.();
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
      };
      const onMove = (ev) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        const next = fitToViewport(
          dragRef.current.origX + dx,
          dragRef.current.origY + dy,
          size.width,
          size.height,
          minimized
        );
        setPos({ x: next.x, y: next.y });
      };
      const onUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [onFocus, pos.x, pos.y, size.width, size.height, minimized]
  );

  const startResize = useCallback(
    (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      onFocus?.();
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origW: size.width,
        origH: size.height,
      };
      const onMove = (ev) => {
        if (!resizeRef.current) return;
        const dw = ev.clientX - resizeRef.current.startX;
        const dh = ev.clientY - resizeRef.current.startY;
        const next = fitToViewport(
          pos.x,
          pos.y,
          resizeRef.current.origW + dw,
          resizeRef.current.origH + dh,
          false
        );
        setSize({ width: next.width, height: next.height });
        setPos({ x: next.x, y: next.y });
      };
      const onUp = () => {
        resizeRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [onFocus, pos.x, pos.y, size.height, size.width]
  );

  return (
    <Paper
      elevation={8}
      className={classes.window}
      style={{
        left: pos.x,
        top: pos.y,
        width: size.width,
        height: minimized ? 40 : size.height,
        zIndex: popup.z || 1600,
      }}
      onMouseDown={onFocus}
    >
      <div className={classes.header} onMouseDown={startDrag}>
        <DragIndicatorIcon fontSize="small" style={{ color: "#94a3b8" }} />
        <Typography className={classes.title} title={popup.name}>
          {popup.name || "Chat"}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMinimized((v) => !v);
          }}
          aria-label={minimized ? "Restaurar" : "Minimizar"}
        >
          {minimized ? <CropSquareIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          aria-label="Cerrar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      {!minimized && (
        <div className={classes.body}>
          <Suspense fallback={null}>
            <Ticket ticketId={popup.uuid} embedded onClose={onClose} />
          </Suspense>
        </div>
      )}
      {!minimized && <div className={classes.resizeHandle} onMouseDown={startResize} />}
    </Paper>
  );
};

export default TicketPopupWindow;
